import React, { useState } from 'react';
import { Mail, Check, Loader2, FileText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'vaad_daily_shiur_email';

const DailyShiurSignupCard = () => {
  const alreadySubscribed = typeof window !== 'undefined' && !!localStorage.getItem(STORAGE_KEY);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(alreadySubscribed ? 'done' : 'idle');
  const [message, setMessage] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (status === 'loading' || status === 'done') return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setMessage('Please enter a valid email');
      return;
    }
    setStatus('loading');
    setMessage('');
    try {
      const res = await fetch(`${API}/api/daily-shiur/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      localStorage.setItem(STORAGE_KEY, email.trim());
      setStatus('done');
      setMessage(
        data.status === 'already-subscribed'
          ? "You're already on the list — see you tomorrow morning."
          : "You're in. Check your inbox for a welcome email."
      );
    } catch (err) {
      setStatus('error');
      setMessage(err.message || 'Something went wrong. Try again in a bit.');
    }
  };

  return (
    <section className="mb-8 sm:mb-12" data-testid="daily-shiur-card">
      <div className="relative overflow-hidden rounded-2xl border border-[#1f2b47]/40 bg-gradient-to-br from-[#001f2a] via-[#16213c] to-[#0f1729] px-4 py-3 sm:p-6">
        <div className="absolute -top-16 -right-16 w-72 h-72 bg-[#9a7b24]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
          {/* Header row: icon + label + heading. On mobile everything
              sits tightly in ONE row so the card doesn't tower over the
              page. Paragraph is hidden below md — the heading + form is
              enough context for a returning subscriber. */}
          <div className="flex items-center gap-3 min-w-0 md:flex-1">
            <div className="shrink-0 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-gradient-to-br from-[#9a7b24] to-[#7d6119] flex items-center justify-center shadow-lg">
              <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[#9a7b24] text-[9px] sm:text-xs font-bold uppercase tracking-[0.2em] leading-none mb-0.5">Daily Shiur Email</div>
              <h2 className="text-white text-[15px] sm:text-xl md:text-2xl font-bold leading-tight md:leading-snug truncate md:whitespace-normal md:overflow-visible">
                A hand-picked shiur, every morning.
              </h2>
            </div>
          </div>

          <p className="hidden md:block text-[#aab2c5] text-sm md:flex-1">
            One shiur every morning — this week&apos;s parsha, upcoming yom tov, or a special zman. MP3 attached so you can listen straight from the email.
          </p>

          <div className="w-full md:w-auto md:min-w-[300px]">
            {status === 'done' ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#1f2b47]/20 border border-white/15 text-[#c7cddc]" data-testid="daily-shiur-success">
                <Check className="w-4 h-4 text-[#9a7b24]" />
                <span className="text-xs sm:text-sm">{message || "You're subscribed."}</span>
              </div>
            ) : (
              <form onSubmit={submit} className="flex gap-2 flex-row">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  disabled={status === 'loading'}
                  data-testid="daily-shiur-email-input"
                  className="flex-1 min-w-0 bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white placeholder:text-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#9a7b24]"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  data-testid="daily-shiur-submit"
                  className="shrink-0 px-4 py-2 bg-[#9a7b24] hover:bg-[#c9a43a] text-white font-semibold text-sm rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {status === 'loading' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Subscribe'
                  )}
                </button>
              </form>
            )}
            {status === 'error' && (
              <p className="text-red-300 text-xs mt-1.5" data-testid="daily-shiur-error">{message}</p>
            )}

            {/* Subtle secondary action — download the full catalog PDF. */}
            <a
              href={`${API}/api/library-email/catalog.pdf`}
              target="_blank"
              rel="noreferrer"
              data-testid="daily-shiur-catalog-link"
              className="mt-1.5 sm:mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#aab2c5] hover:text-white underline underline-offset-2 decoration-dotted"
            >
              <FileText className="w-3 h-3" />
              Or download the full catalog (PDF)
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DailyShiurSignupCard;
